import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import EastIcon from "@mui/icons-material/East";
import {
  Checkbox,
  Divider,
  FormHelperText,
  Grid,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { ColorRgbButton } from "components/input/colorButton/ColorRgbButton";
import { useAuthUser } from "hooks/authentication";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { type Dispatch, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColorCieLab } from "service/color-math";
import { checkInternalUser } from "service/utils/get-user-type";
import { ColorEffectFilter } from "./ColorEffectFilter";
import { ColorFilterLab } from "./ColorFilterLab";
import { ColorFilterLch } from "./ColorFilterLch";
import { ColorFilterNcs } from "./ColorFilterNcs";
import { ColorFilterNcsDisclaimer } from "./ColorFilterNcsDisclaimer";
import { ColorFilterNcsExplanation } from "./ColorFilterNcsExplanation";
import { ColorFilterRalClassic } from "./ColorFilterRalClassic";
import { ColorFilterRalDesignPlus } from "./ColorFilterRalDesignPlus";
import { ColorFilterRalDisclaimers } from "./ColorFilterRalDisclaimers";
import { ColorGroupingFilter } from "./ColorGroupingFilter";
import { type ColorSystemOption, ColorSystemSelect } from "./ColorSystemSelect";
import { ColorTagFilter } from "./ColorTagFilter";
import { CovestroColorCodeFilter } from "./CovestroColorCodeFilter";
import { OemColorCodeFilter } from "./OemColorCodeFilter";
import { OpacityFilter } from "./OpacityFilter";
import { OEM_COLOR_CODE_SYSTEM, type OemColorValue } from "./oem-color";
import { PreliminaryColorCodeFilter } from "./PreliminaryColorCodeFilter";
import { YearDevelopemntFilter } from "./YearDevelopmentFilter";

const colorSystems: Record<string, ColorSystemOption> = {
  none: {
    value: "none",
    label: "color_system_none",
  },
  preliminary_color_code: {
    value: "preliminary_color_code",
    label: "color_system_preliminary_color_code",
    example: "color_system_example_preliminary",
  },
  covestro_color_code: {
    value: "covestro_color_code",
    label: "color_system_covestro_color_code",
    example: "color_system_example_covestro",
  },
  oem_color_code: {
    value: OEM_COLOR_CODE_SYSTEM,
    label: "color_system_oem_color_code",
    example: "color_system_example_oem",
  },
  cie_lab: {
    value: "cie_lab",
    label: "color_system_cie_lab",
    example: "color_system_example_cie_lab",
  },
  cie_lch: {
    value: "cie_lch",
    label: "color_system_cie_lch",
    example: "color_system_example_cie_lch",
  },
  ncs: {
    value: "ncs",
    label: "color_system_ncs",
    trademark: "®©",
    example: "color_system_example_ncs",
  },
  ral_classic: {
    value: "ral_classic",
    label: "color_system_ral_classic",
    example: "color_system_example_ral_classic",
    disabled: true,
  },
  ral_design_plus: {
    value: "ral_design_plus",
    label: "color_system_ral_design_plus",
    example: "color_system_example_ral_design_plus",
    disabled: true,
  },
  color_group: {
    value: "color_group",
    label: "color_system_color_group",
  },
  pantone_c: {
    value: "pantone_c",
    label: "color_system_pantone_c",
    example: "color_system_example_pantone_c",
    disabled: true,
  },
  pantone_u: {
    value: "pantone_u",
    label: "color_system_pantone_u",
    example: "color_system_example_pantone_u",
    disabled: true,
  },
  pantone_tpx: {
    value: "pantone_tpx",
    label: "color_system_pantone_tpx",
    example: "color_system_example_pantone_tpx",
    disabled: true,
  },
  pantone_tcx: {
    value: "pantone_tcx",
    label: "color_system_pantone_tcx",
    example: "color_system_example_pantone_tcx",
    disabled: true,
  },
  munsell: {
    value: "munsell",
    label: "color_system_munsell",
    example: "color_system_example_munsell",
    disabled: true,
  },
};

const supportsColorValueEditing = [
  colorSystems.cie_lab.value,
  colorSystems.cie_lch.value,
];

const usesLabColors = [
  colorSystems.oem_color_code.value,
  colorSystems.cie_lab.value,
  colorSystems.cie_lch.value,
  colorSystems.ncs.value,
  colorSystems.ral_classic.value,
  colorSystems.ral_design_plus.value,
  colorSystems.pantone_c.value,
  colorSystems.pantone_u.value,
  colorSystems.pantone_tpx.value,
  colorSystems.pantone_tcx.value,
  colorSystems.munsell.value,
];

export interface NamedColor {
  value: ColorCieLab;
  name: string;
  measurementCondition?: string;
  warning?: string;
  code?: string;
  deltaE?: number;
}

export interface ColorFilterValue {
  color?: NamedColor;
  oemColor?: OemColorValue;
  finalColorCode?: Array<AutocompleteData>;
  yearOfDevelopment?: Array<AutocompleteData>;
  preliminaryColorCode?: Array<AutocompleteData>;
  colorSystem: string;
  colorSystemCode?: string;
}

export interface ColorFilterProps {
  onChange: (color: ColorFilterValue) => void;
  value: ColorFilterValue | undefined;

  opacityValue: Array<AutocompleteData> | undefined;
  onOpacityValueChange: Dispatch<Array<AutocompleteData>>;

  colorEffectValue: Array<AutocompleteData> | undefined;
  onColorEffectValueChange: Dispatch<Array<AutocompleteData>>;

  colorTagValue: Array<AutocompleteData> | undefined;
  onColorTagValueChange: Dispatch<Array<AutocompleteData>>;

  colorGroupValue: Array<AutocompleteData> | undefined;
  onColorGroupValueChange: Dispatch<Array<AutocompleteData>>;

  columns?: number;

  isColorSystem: string | undefined;
  setIsColorSystem: Dispatch<string | undefined>;
}

// The user loads after the first render, so an OEM pick is only hidden for non-internal users, never dropped
const visibleColorSystem = ({
  pickedColorSystem,
  isInternal,
}: {
  pickedColorSystem: string;
  isInternal: boolean;
}): string =>
  pickedColorSystem === OEM_COLOR_CODE_SYSTEM && !isInternal
    ? colorSystems.none.value
    : pickedColorSystem;

const matchedSystemSystems = [
  colorSystems.ral_classic.value,
  colorSystems.ral_design_plus.value,
  colorSystems.ncs.value,
];

const matchedSystemLabelKey = (selectedColorSystem: string): string => {
  if (selectedColorSystem === colorSystems.ncs.value) {
    return "filter_matched_NCS_system";
  }
  return selectedColorSystem === colorSystems.ral_classic.value
    ? "filter_matched_RAL_CLASSIC_system"
    : "filter_matched_RAL_DESIGN_system";
};

const ColorMeasurementNotes = ({
  selectedColorSystem,
  measurementCondition,
  opacityValue,
  isColorSystem,
  setIsColorSystem,
}: {
  selectedColorSystem: string;
  measurementCondition: string;
  opacityValue: Array<AutocompleteData> | undefined;
  isColorSystem: string | undefined;
  setIsColorSystem: Dispatch<string | undefined>;
}) => {
  const { t } = useTranslation();
  return (
    <Grid mt={-2} mb={-1} size={{ xs: 12, lg: 6, xl: 3 }}>
      <FormHelperText>{measurementCondition}</FormHelperText>
      {(opacityValue?.some((v) => v.value !== "OPAQUE") ||
        !opacityValue?.length) && (
        <FormHelperText error>{t("filter_color_search_notice")}</FormHelperText>
      )}
      {matchedSystemSystems.includes(selectedColorSystem) && (
        <ListItem
          sx={(theme) => ({ border: `1px solid ${theme.palette.divider}` })}
          secondaryAction={
            <Checkbox
              onChange={(e) =>
                setIsColorSystem(
                  e.target.checked
                    ? colorSystems[selectedColorSystem].value
                    : undefined,
                )
              }
              checked={!!isColorSystem}
            />
          }
        >
          <ListItemText>
            {t(matchedSystemLabelKey(selectedColorSystem))}
          </ListItemText>
        </ListItem>
      )}
    </Grid>
  );
};

const ColorSystemDisclaimers = ({
  selectedColorSystem,
}: {
  selectedColorSystem: string;
}) => (
  <>
    {selectedColorSystem === colorSystems.ncs.value && (
      <>
        <Grid size={{ xs: 12, lg: 6, xl: 3 }}>
          <ColorFilterNcsDisclaimer />
        </Grid>
        <Grid size={{ xs: 12, lg: 6, xl: 3 }}>
          <ColorFilterNcsExplanation />
        </Grid>
      </>
    )}
    {[
      colorSystems.ral_design_plus.value,
      colorSystems.ral_classic.value,
    ].includes(selectedColorSystem) && <ColorFilterRalDisclaimers />}
  </>
);

export const ColorFilter = ({
  onChange,
  value,
  opacityValue,
  onOpacityValueChange,
  colorEffectValue,
  onColorEffectValueChange,
  colorTagValue,
  onColorTagValueChange,
  colorGroupValue,
  onColorGroupValueChange,
  columns = 12,
  isColorSystem,
  setIsColorSystem,
}: ColorFilterProps) => {
  const { t } = useTranslation();
  const { settings, setSettings } =
    useContext<SettingsContextValue>(SettingsContext);
  const { user } = useAuthUser();
  const isInternal: boolean = checkInternalUser(user, settings);
  const [pickedColorSystem, setSelectedColorSystem] = useState(
    (value?.colorSystem || isColorSystem) ?? colorSystems.none.value,
  );
  const selectedColorSystem = visibleColorSystem({
    pickedColorSystem,
    isInternal,
  });
  const measurementCondition: string = `${t("src_filter_block_color_measurement")}`;
  const colorSystemOptions = Object.values(colorSystems).filter(
    (option) => isInternal || option.value !== OEM_COLOR_CODE_SYSTEM,
  );

  const onSelectedColorSystemChange = (colorSystem: string) => {
    setSelectedColorSystem(colorSystem);
    setSettings({
      ...settings,
      yearOfDevelopment: "",
      preliminaryColorCode: [],
    });
    onChange({ colorSystem: colorSystem });
  };

  const onDelete = () => {
    setSelectedColorSystem(colorSystems.none.value);
    onChange({ colorSystem: colorSystems.none.value });
  };
  const onChangeColor = (color: NamedColor | undefined) => {
    onChange({
      color: color ? { ...color, measurementCondition } : undefined,
      finalColorCode: undefined,
      colorSystem: selectedColorSystem,
    });
  };
  const onChangeOemColor = (oemColor: OemColorValue) => {
    onChange({
      color: undefined,
      oemColor,
      finalColorCode: undefined,
      colorSystem: selectedColorSystem,
    });
  };
  const onChangeColorCode = (finalColorCode: Array<AutocompleteData>) => {
    onChange({
      color: undefined,
      finalColorCode,
      colorSystem: colorSystems.covestro_color_code.value,
    });
  };

  const onChangeYearOfDevelopment = (
    yearOfDevelopment: Array<AutocompleteData>,
  ) => {
    const yod = JSON.parse(JSON.stringify(value));
    yod.yearOfDevelopment =
      yearOfDevelopment.length > 0 ? [yearOfDevelopment[0]] : undefined;
    onChange(yod);
    if (yearOfDevelopment.length > 0) {
      setSettings({
        ...settings,
        yearOfDevelopment: yearOfDevelopment[0].value,
      });
    } else {
      setSettings({ ...settings, yearOfDevelopment: "" });
    }
  };

  const onChangePreliminaryColorCode = (
    preliminaryColorCode: Array<AutocompleteData>,
  ) => {
    const pcc = JSON.parse(JSON.stringify(value));
    pcc.preliminaryColorCode = preliminaryColorCode || undefined;
    onChange(pcc);
    setSettings({
      ...settings,
      preliminaryColorCode: pcc.preliminaryColorCode,
    });
  };

  return (
    <Grid mt={-2} mb={2} container spacing={2} columns={columns}>
      <Grid size={{ xs: 12, md: 6, xl: 3 }}>
        <Stack spacing={2} direction="row" alignItems="center">
          <ColorRgbButton
            sx={{ flexShrink: 0 }}
            onChange={onChangeColor}
            onDelete={onDelete}
            disabled={!supportsColorValueEditing.includes(selectedColorSystem)}
            color={value?.color?.value ?? value?.oemColor?.cieLab}
            t={t}
          ></ColorRgbButton>
          <ColorSystemSelect
            options={colorSystemOptions}
            onChange={onSelectedColorSystemChange}
            value={selectedColorSystem}
            t={t}
          />
        </Stack>
      </Grid>
      {selectedColorSystem === colorSystems.preliminary_color_code.value && (
        <Grid size={{ xs: 12, md: 6, xl: 3 }}>
          {isInternal && (
            <>
              <YearDevelopemntFilter
                value={value?.yearOfDevelopment}
                onChange={onChangeYearOfDevelopment}
              />
              <FormHelperText
                sx={{ color: "#009FE4", display: "flex", paddingLeft: "10px" }}
              >
                <Typography
                  margin={"5px 0"}
                  fontSize="var(--body-b3-12-regular-size, 12px)"
                >
                  {t("filter_cdc_color_lap")}
                </Typography>{" "}
                <EastIcon
                  sx={{
                    margin: "8px 3px 0 3px",
                    width: "16px",
                    height: "12px",
                  }}
                />{" "}
                <Typography
                  margin={"5px 0"}
                  fontSize="var(--body-b3-12-regular-size, 12px)"
                >
                  {t("filter_cdc_color_lap_misecellaneous")}
                </Typography>
              </FormHelperText>
            </>
          )}
          <PreliminaryColorCodeFilter
            value={value?.preliminaryColorCode}
            onChange={onChangePreliminaryColorCode}
          />
        </Grid>
      )}
      {selectedColorSystem === colorSystems.covestro_color_code.value && (
        <CovestroColorCodeFilter
          value={value?.finalColorCode}
          onChange={onChangeColorCode}
          t={t}
        />
      )}
      {selectedColorSystem === colorSystems.cie_lab.value && (
        <ColorFilterLab value={value?.color?.value} onChange={onChangeColor} />
      )}
      {selectedColorSystem === colorSystems.cie_lch.value && (
        <ColorFilterLch value={value?.color?.value} onChange={onChangeColor} />
      )}
      {isInternal &&
        selectedColorSystem === colorSystems.oem_color_code.value && (
          <OemColorCodeFilter
            value={value?.oemColor}
            onChange={onChangeOemColor}
          />
        )}
      {selectedColorSystem === colorSystems.ncs.value && (
        <ColorFilterNcs value={value?.color} onChange={onChangeColor} />
      )}
      {selectedColorSystem === colorSystems.ral_classic.value && (
        <ColorFilterRalClassic value={value?.color} onChange={onChangeColor} />
      )}
      {selectedColorSystem === colorSystems.ral_design_plus.value && (
        <ColorFilterRalDesignPlus
          value={value?.color}
          onChange={onChangeColor}
        />
      )}
      {selectedColorSystem === colorSystems.color_group.value && (
        <Grid size={{ xs: 12, md: 6, xl: 3 }}>
          <ColorGroupingFilter
            value={colorGroupValue}
            onChange={onColorGroupValueChange}
          />
        </Grid>
      )}
      {usesLabColors.includes(selectedColorSystem) && (
        <ColorMeasurementNotes
          selectedColorSystem={selectedColorSystem}
          measurementCondition={measurementCondition}
          opacityValue={opacityValue}
          isColorSystem={isColorSystem}
          setIsColorSystem={setIsColorSystem}
        />
      )}
      <ColorSystemDisclaimers selectedColorSystem={selectedColorSystem} />
      <Grid size={12}>
        <Divider light />
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 3 }}>
        <OpacityFilter
          value={opacityValue}
          onChange={onOpacityValueChange}
        ></OpacityFilter>
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 3 }}>
        <ColorEffectFilter
          value={colorEffectValue}
          onChange={onColorEffectValueChange}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6, xl: 3 }}>
        <ColorTagFilter
          value={colorTagValue}
          onChange={onColorTagValueChange}
        />
      </Grid>
    </Grid>
  );
};
